import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, BASE_URL, colors } from "./shared/constants";
import { styles } from "./shared/styles";
import { useIsMobile } from "./shared/utils";
import {
  LuBuilding2,
  LuMapPin,
  LuClipboardList,
  LuWrench,
  LuCalendar,
  LuFolder,
  LuFlame,
  LuTag,
  LuLayoutDashboard,
  LuChartBar,
  LuTriangleAlert,
  LuHouse,
  LuCalendarDays,
  LuCalendarClock,
  LuFileText,
  LuUser,
  LuMenu,
  LuX,
  LuLogOut,
  LuMap,
  LuUsers,
  LuSignature,
  LuClipboardPen,
  LuArchive,
  LuReceiptText,
  LuChevronDown,
  LuTruck,
  LuShieldCheck,
  LuBanknote,
  LuBell,
  LuInfo,
  LuUserCog,
  LuGauge,
  LuUserPlus,
} from "react-icons/lu";
import { LoginPage } from "./auth/LoginPage";
import { CustomerAccounts } from "./accounts/CustomerAccounts";
import { AccountDetail } from "./accounts/AccountDetail";
import { SitesPage } from "./sites/SitesPage";
import { InspectionsPage } from "./inspections/InspectionsPage";
import { EFSMSettings } from "./settings/EFSMSettings";
import { AssetTypesSettings } from "./settings/AssetTypesSettings";
import { FailTypesSettings } from "./settings/FailTypesSettings";
import { AssetInfoSettings } from "./settings/AssetInfoSettings";
import { ServicePricesSettings } from "./settings/ServicePricesSettings";
import { SuppliersSettings } from "./settings/SuppliersSettings";
import { UsersSettings } from "./settings/UsersSettings";
import { AFSSPage } from "./customers/AFSSPage";
import { MonthlyServicePage } from "./customers/MonthlyServicePage";
import { ManagementHome } from "./dashboard/ManagementHome";
import { ManagementDashboard } from "./dashboard/ManagementDashboard";
import { StatisticsPage } from "./dashboard/StatisticsPage";
import { SchedulingPage } from "./dashboard/SchedulingPage";
import { FieldTechDashboard } from "./fieldtech/FieldTechDashboard";
import { FieldTechRoster } from "./fieldtech/FieldTechRoster";
import { FieldTechSchedule } from "./fieldtech/FieldTechSchedule";
import { FieldTechReports } from "./fieldtech/FieldTechReports";
import { FieldTechProfile } from "./fieldtech/FieldTechProfile";
import { FieldTechStatistics } from "./fieldtech/FieldTechStatistics";
import { RecordsPage } from "./records/RecordsPage";
import { StaffRecords } from "./dashboard/StaffRecords";
import { ManagementReports } from "./dashboard/ManagementReports";
import { ServiceRequestsPage } from "./requests/ServiceRequestsPage";
import { TemplatesPage } from "./templates/TemplatesPage";
import { RectificationsPage } from "./rectifications/RectificationsPage";
import { AssignWorkPage } from "./dashboard/AssignWorkPage";
import { FlowTestsPage } from "./flowtests/FlowTestsPage";
import { GeneralQuotesPage } from "./quotes/GeneralQuotesPage";
import { FieldTechCompleted } from "./fieldtech/FieldTechCompleted";
import { DefectsRegisterPage } from "./defects/DefectsRegisterPage";
import { BillsPage } from "./dashboard/BillsPage";
import { NoticesPage } from "./dashboard/NoticesPage";

export default function App() {
  const [activePage, setActivePage] = useState(() => {
    const role = localStorage.getItem("sfp_role");
    if (role === "field_tech") return "ft-dashboard";
    if (role === "management") return "home";
    return "accounts";
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [directInspectionId, setDirectInspectionId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [billsInitialCat, setBillsInitialCat] = useState("basic");
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem("sfp_nav_collapsed");
      return saved ? JSON.parse(saved) : {
        "Field Tech": true,
        "Management": true,
        "Customers": true,
        "Rectifications": true,
        "Templates": true,
        "Settings": true,
        "Admin": true,
      };
    } catch { return {}; }
  });

  useEffect(() => {
    // Intercept 401 responses anywhere in the app, refresh the token, and retry
    const interceptorId = axios.interceptors.response.use(
      response => response,
      async error => {
        const original = error.config;
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;
          const refresh = localStorage.getItem("sfp_refresh_token");
          if (refresh) {
            try {
              const res = await axios.post(`${BASE_URL}/auth/token/refresh/`, { refresh });
              const newToken = res.data.access;
              localStorage.setItem("sfp_token", newToken);
              axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
              original.headers = original.headers || {};
              original.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(original);
            } catch {
              localStorage.removeItem("sfp_token");
              localStorage.removeItem("sfp_refresh_token");
              localStorage.removeItem("sfp_role");
              localStorage.removeItem("sfp_username");
              localStorage.removeItem("sfp_first_name");
              delete axios.defaults.headers.common["Authorization"];
              setIsAuthenticated(false);
            }
          } else {
            setIsAuthenticated(false);
          }
        }
        return Promise.reject(error);
      }
    );

    const token = localStorage.getItem("sfp_token");
    const savedRole = localStorage.getItem("sfp_role");
    if (savedRole) setUserRole(savedRole);
    const refreshToken = localStorage.getItem("sfp_refresh_token");

    if (!token && !refreshToken) {
      setIsAuthenticated(false);
      setAuthChecked(true);
      return () => axios.interceptors.response.eject(interceptorId);
    }

    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // Field techs can't access /accounts/ so use a different auth check
    const authCheckUrl =
      savedRole === "field_tech" ? `${API}/inspections/` : `${API}/accounts/`;

    axios
      .get(authCheckUrl)
      .then(() => {
        setIsAuthenticated(true);
        setAuthChecked(true);
      })
      .catch(async () => {
        if (refreshToken) {
          try {
            const res = await axios.post(`${BASE_URL}/auth/token/refresh/`, {
              refresh: refreshToken,
            });
            const newToken = res.data.access;
            localStorage.setItem("sfp_token", newToken);
            axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
            const storedRole = localStorage.getItem("sfp_role");
            setUserRole(storedRole);
            setIsAuthenticated(true);
            setAuthChecked(true);
          } catch {
            localStorage.removeItem("sfp_token");
            localStorage.removeItem("sfp_refresh_token");
            localStorage.removeItem("sfp_role");
            localStorage.removeItem("sfp_username");
            setIsAuthenticated(false);
            setAuthChecked(true);
          }
        } else {
          setIsAuthenticated(false);
          setAuthChecked(true);
        }
      });

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, sidebarOpen]);

  if (!authChecked)
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.darkGrey,
          color: "white",
          fontSize: "16px",
        }}
      >
        Loading...
      </div>
    );

  if (!isAuthenticated)
    return (
      <LoginPage
        onLogin={(role) => {
          setIsAuthenticated(true);
          setUserRole(role);
          setActivePage(role === "field_tech" ? "ft-dashboard" : role === "management" ? "home" : "accounts");
        }}
      />
    );

  const iconProps = { size: 16, strokeWidth: 1.75 };

  // Field tech nav items
  const fieldTechNavItems = [
    { id: "ft-dashboard", label: "Home", icon: <LuHouse {...iconProps} /> },
    { id: "ft-roster", label: "Roster", icon: <LuCalendarDays {...iconProps} /> },
    { id: "ft-schedule", label: "Service Schedule", icon: <LuCalendarClock {...iconProps} /> },
    { id: "ft-reports", label: "Reports", icon: <LuFileText {...iconProps} /> },
    { id: "ft-statistics", label: "Statistics", icon: <LuChartBar {...iconProps} /> },
    { id: "ft-completed", label: "Completed", icon: <LuArchive {...iconProps} /> },
    { id: "ft-profile", label: "Profile", icon: <LuUser {...iconProps} /> },
  ];

  // Management/admin nav items
  const navItems = [
    { id: "accounts", label: "Customer Accounts", icon: <LuBuilding2 {...iconProps} /> },
    { id: "sites", label: "Sites", icon: <LuMapPin {...iconProps} /> },
    { id: "inspections", label: "Inspections", icon: <LuClipboardList {...iconProps} /> },
    { id: "requests", label: "Customer Requests", icon: <LuWrench {...iconProps} /> },
    { id: "scheduling", label: "Scheduling", icon: <LuCalendar {...iconProps} /> },
    { id: "field-reports", label: "Reports", icon: <LuFileText {...iconProps} /> },
    { id: "afss", label: "AFSS", icon: <LuShieldCheck {...iconProps} /> },
    { id: "monthly-service", label: "Monthly Services", icon: <LuCalendarClock {...iconProps} /> },
  ];

  const adminItems = [
    { id: "assign-work", label: "Assign Work", icon: <LuUserCog {...iconProps} /> },
    { id: "records", label: "Records", icon: <LuFolder {...iconProps} /> },
  ];

  const settingsItems = [
    { id: "efsm-settings", label: "Fire Safety Measures", icon: <LuFlame {...iconProps} /> },
    { id: "asset-types", label: "Asset Types", icon: <LuTag {...iconProps} /> },
    { id: "fail-types", label: "Fail Types", icon: <LuTriangleAlert {...iconProps} /> },
    { id: "asset-info", label: "Asset Info Fields", icon: <LuInfo {...iconProps} /> },
    { id: "service-prices", label: "Service Prices", icon: <LuReceiptText {...iconProps} /> },
    { id: "suppliers", label: "Suppliers", icon: <LuTruck {...iconProps} /> },
    { id: "user-management", label: "Users", icon: <LuUserPlus {...iconProps} /> },
  ];

  const templateItems = [
    { id: "form-templates", label: "Form Assets", icon: <LuSignature {...iconProps} /> },
  ];

  const rectificationItems = [
    { id: "rectifications", label: "Rectifications", icon: <LuClipboardPen {...iconProps} /> },
    { id: "general-quotes", label: "General Quotes", icon: <LuReceiptText {...iconProps} /> },
    { id: "flow-tests", label: "Flow Tests", icon: <LuGauge {...iconProps} /> },
  ];

  const managementItems = [
    { id: "home", label: "Home", icon: <LuMap {...iconProps} /> },
    { id: "dashboard", label: "Dashboard", icon: <LuLayoutDashboard {...iconProps} /> },
    { id: "statistics", label: "Statistics", icon: <LuChartBar {...iconProps} /> },
    { id: "staff-records", label: "Staff Records", icon: <LuUsers {...iconProps} /> },
    { id: "defects", label: "Defect Register", icon: <LuTriangleAlert {...iconProps} /> },
    { id: "bills", label: "Bills Tracker", icon: <LuBanknote {...iconProps} /> },
    { id: "notices", label: "Notices", icon: <LuBell {...iconProps} /> },
  ];

  const handleSignOut = () => {
    localStorage.removeItem("sfp_token");
    localStorage.removeItem("sfp_refresh_token");
    localStorage.removeItem("sfp_role");
    localStorage.removeItem("sfp_username");
    localStorage.removeItem("sfp_first_name");
    delete axios.defaults.headers.common["Authorization"];
    window.location.reload();
  };

  const handleNavClick = (id, opts = {}) => {
    setActivePage(id);
    setSelectedAccount(null);
    setDirectInspectionId(null);
    if (opts.cat) setBillsInitialCat(opts.cat);
    if (isMobile) setSidebarOpen(false);
  };

  const handleStartInspection = (id) => {
    setDirectInspectionId(id);
    setActivePage("inspections");
    if (isMobile) setSidebarOpen(false);
  };

  const toggleSection = (label) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      try { localStorage.setItem("sfp_nav_collapsed", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const isSectionCollapsed = (label) => {
    return !!collapsedSections[label];
  };

  const sidebarStyle = {
    ...styles.sidebar,
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 200,
    overflow: "hidden",
    transform:
      isMobile && !sidebarOpen ? "translateX(-240px)" : "translateX(0)",
    transition: "transform 0.25s ease",
    boxShadow: isMobile && sidebarOpen ? "4px 0 20px rgba(0,0,0,0.3)" : "none",
  };

  const navItemStyle = (active, isFieldTech) => ({
    ...styles.navItem(active),
    padding: isMobile ? "12px 16px" : "10px 16px",
    backgroundColor: active
      ? isFieldTech
        ? colors.teal
        : colors.softBlue
      : "transparent",
    color: active ? colors.white : "rgba(255,255,255,0.65)",
    fontWeight: active ? "600" : "400",
  });

  return (
    <div
      style={{
        height: "100vh",
        overflow: "hidden",
        backgroundColor: colors.lightGrey,
      }}
    >
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 199,
          }}
        />
      )}

      <div style={sidebarStyle}>
        <div style={{ ...styles.logoArea, position: "relative" }}>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.6)",
                cursor: "pointer",
                padding: "4px",
                lineHeight: 1,
                minHeight: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <LuX size={18} />
            </button>
          )}
          <div style={{ fontSize: "26px" }}>🔥</div>
          <div style={styles.logoText}>Standard Fire Protection</div>
          <button
            onClick={handleSignOut}
            style={{
              marginTop: "10px",
              backgroundColor: "rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(255,255,255,0.12)",
              padding: "6px 14px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: "500",
              width: "100%",
              minHeight: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
          >
            <LuLogOut size={13} />
            Sign Out
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {userRole === "field_tech" ? (
          <div style={styles.navSection}>
            <div
              style={{ ...styles.navLabel, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
              onClick={() => toggleSection("Field Tech")}
            >
              <span>Field Tech</span>
              <LuChevronDown size={13} style={{ opacity: 0.6, transform: isSectionCollapsed("Field Tech") ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }} />
            </div>
            {!isSectionCollapsed("Field Tech") && fieldTechNavItems.map((item) => (
              <div
                key={item.id}
                style={navItemStyle(activePage === item.id, true)}
                onClick={() => handleNavClick(item.id)}
              >
                <span style={{ marginRight: "10px" }}>{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>
        ) : (
          <>
            {userRole === "management" && (
              <div style={styles.navSection}>
                <div
                  style={{ ...styles.navLabel, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                  onClick={() => toggleSection("Management")}
                >
                  <span>Management</span>
                  <LuChevronDown size={13} style={{ opacity: 0.6, transform: isSectionCollapsed("Management") ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }} />
                </div>
                {!isSectionCollapsed("Management") && managementItems.map((item) => (
                  <div
                    key={item.id}
                    style={navItemStyle(activePage === item.id, false)}
                    onClick={() => handleNavClick(item.id)}
                  >
                    <span style={{ marginRight: "10px" }}>{item.icon}</span>
                    {item.label}
                  </div>
                ))}
              </div>
            )}

            <div style={styles.navSection}>
              <div
                style={{ ...styles.navLabel, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                onClick={() => toggleSection("Customers")}
              >
                <span>Customers</span>
                <LuChevronDown size={13} style={{ opacity: 0.6, transform: isSectionCollapsed("Customers") ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }} />
              </div>
              {!isSectionCollapsed("Customers") && navItems.map((item) => (
                <div
                  key={item.id}
                  style={navItemStyle(activePage === item.id, false)}
                  onClick={() => handleNavClick(item.id)}
                >
                  <span style={{ marginRight: "10px" }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>

            <div style={styles.navSection}>
              <div
                style={{ ...styles.navLabel, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                onClick={() => toggleSection("Rectifications")}
              >
                <span>Rectifications</span>
                <LuChevronDown size={13} style={{ opacity: 0.6, transform: isSectionCollapsed("Rectifications") ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }} />
              </div>
              {!isSectionCollapsed("Rectifications") && rectificationItems.map((item) => (
                <div
                  key={item.id}
                  style={navItemStyle(activePage === item.id, false)}
                  onClick={() => handleNavClick(item.id)}
                >
                  <span style={{ marginRight: "10px" }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>

            <div style={styles.navSection}>
              <div
                style={{ ...styles.navLabel, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                onClick={() => toggleSection("Templates")}
              >
                <span>Templates</span>
                <LuChevronDown size={13} style={{ opacity: 0.6, transform: isSectionCollapsed("Templates") ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }} />
              </div>
              {!isSectionCollapsed("Templates") && templateItems.map((item) => (
                <div
                  key={item.id}
                  style={navItemStyle(activePage === item.id, false)}
                  onClick={() => handleNavClick(item.id)}
                >
                  <span style={{ marginRight: "10px" }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>

            <div style={styles.navSection}>
              <div
                style={{ ...styles.navLabel, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                onClick={() => toggleSection("Settings")}
              >
                <span>Settings</span>
                <LuChevronDown size={13} style={{ opacity: 0.6, transform: isSectionCollapsed("Settings") ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }} />
              </div>
              {!isSectionCollapsed("Settings") && settingsItems.map((item) => (
                <div
                  key={item.id}
                  style={navItemStyle(activePage === item.id, false)}
                  onClick={() => handleNavClick(item.id)}
                >
                  <span style={{ marginRight: "10px" }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>

            <div style={styles.navSection}>
              <div
                style={{ ...styles.navLabel, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                onClick={() => toggleSection("Admin")}
              >
                <span>Admin</span>
                <LuChevronDown size={13} style={{ opacity: 0.6, transform: isSectionCollapsed("Admin") ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }} />
              </div>
              {!isSectionCollapsed("Admin") && adminItems.map((item) => (
                <div
                  key={item.id}
                  style={navItemStyle(activePage === item.id, false)}
                  onClick={() => handleNavClick(item.id)}
                >
                  <span style={{ marginRight: "10px" }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          </>
        )}
        </div>
      </div>

      <div style={{ ...styles.main, marginLeft: isMobile ? 0 : "240px", height: "100vh" }}>
        {/* Mobile hamburger FAB */}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              position: "fixed",
              bottom: "24px",
              right: "24px",
              zIndex: 100,
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor:
                userRole === "field_tech" ? colors.teal : colors.red,
              color: "white",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "auto",
            }}
          >
            <LuMenu size={22} />
          </button>
        )}

        {/* Management/admin pages */}
        {activePage === "home" && <ManagementHome onNavigate={handleNavClick} />}
        {activePage === "accounts" && !selectedAccount && (
          <CustomerAccounts onSelect={(id) => setSelectedAccount(id)} />
        )}
        {activePage === "accounts" && selectedAccount && (
          <AccountDetail
            accountId={selectedAccount}
            onBack={() => setSelectedAccount(null)}
          />
        )}
        {activePage === "sites" && <SitesPage />}
        {activePage === "requests" && <ServiceRequestsPage />}
        {activePage === "inspections" && <InspectionsPage initialInspectionId={directInspectionId} onDone={() => handleNavClick("ft-dashboard")} />}
        {activePage === "efsm-settings" && <EFSMSettings />}
        {activePage === "asset-types" && <AssetTypesSettings />}
        {activePage === "fail-types" && <FailTypesSettings />}
        {activePage === "asset-info" && <AssetInfoSettings />}
        {activePage === "service-prices" && <ServicePricesSettings />}
        {activePage === "suppliers" && <SuppliersSettings />}
        {activePage === "user-management" && <UsersSettings />}
        {activePage === "dashboard" && <ManagementDashboard onNavigate={handleNavClick} />}
        {activePage === "statistics" && <StatisticsPage />}
        {activePage === "scheduling" && <SchedulingPage />}
        {activePage === "records" && <RecordsPage />}
        {activePage === "assign-work" && <AssignWorkPage />}
        {activePage === "staff-records" && <StaffRecords />}
        {activePage === "field-reports" && <ManagementReports />}
        {activePage === "afss" && <AFSSPage onNavigate={handleNavClick} />}
        {activePage === "monthly-service" && <MonthlyServicePage />}
        {activePage === "form-templates" && <TemplatesPage />}
        {activePage === "rectifications" && <RectificationsPage />}
        {activePage === "general-quotes" && <GeneralQuotesPage />}
        {activePage === "flow-tests" && <FlowTestsPage />}
        {activePage === "defects" && <DefectsRegisterPage />}
        {activePage === "bills" && <BillsPage initialCatTab={billsInitialCat} />}
        {activePage === "notices" && <NoticesPage />}

        {/* Field tech pages */}
        {activePage === "ft-dashboard" && (
          <FieldTechDashboard onNavigate={handleNavClick} onStartInspection={handleStartInspection} />
        )}
        {activePage === "ft-roster" && <FieldTechRoster />}
        {activePage === "ft-schedule" && <FieldTechSchedule />}
        {activePage === "ft-reports" && <FieldTechReports />}
        {activePage === "ft-completed" && <FieldTechCompleted />}
        {activePage === "ft-profile" && <FieldTechProfile />}
        {activePage === "ft-statistics" && <FieldTechStatistics />}
      </div>
    </div>
  );
}
